import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  NOTIFICATION_PAGE_SIZE,
} from '../api/notifications';
import type { NotificationItem } from './types';
import { useSSE } from '../chat/useSSE';
import { pushToast } from './toastStore';

export interface NotificationContextValue {
  items: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  hasMore: boolean;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const NotificationContext = createContext<NotificationContextValue | null>(null);

/** Trailing-debounce so a burst of SSE frames (e.g. a streaming answer) yields
 *  a single refetch after the stream settles — which is when the backend has
 *  persisted the completion notification. */
const REFETCH_DEBOUNCE_MS = 1500;

export function NotificationProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const loadingRef = useRef(false);
  const unreadCountRef = useRef(0);
  const initialLoadedRef = useRef(false);
  useEffect(() => {
    unreadCountRef.current = unreadCount;
  }, [unreadCount]);

  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const togglePanel = useCallback(() => setPanelOpen((o) => !o), []);

  const refetch = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const page = await fetchNotifications(0, NOTIFICATION_PAGE_SIZE);
      const newUnread = page.items.filter((n) => !n.read).length;
      // Toast only on a genuine increase, and never for pre-existing unread on
      // the very first load.
      if (initialLoadedRef.current && newUnread > unreadCountRef.current) {
        const newest = page.items.find((n) => !n.read);
        if (newest) {
          pushToast({
            title: newest.title || 'New notification',
            message: newest.description,
            onClick: openPanel,
          });
        }
      }
      initialLoadedRef.current = true;
      setItems(page.items);
      setTotalCount(page.totalCount);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notifications] refetch failed', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [openPanel]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || items.length >= totalCount) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const page = await fetchNotifications(items.length, NOTIFICATION_PAGE_SIZE);
      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...page.items.filter((p) => !seen.has(p.id))];
      });
      setTotalCount(page.totalCount);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notifications] loadMore failed', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [items.length, totalCount]);

  const markRead = useCallback(async (id: string) => {
    let reverted = false;
    setItems((prev) =>
      prev.map((n) => {
        if (n.id === id && !n.read) {
          reverted = true;
          return { ...n, read: true };
        }
        return n;
      }),
    );
    if (!reverted) return; // already read
    try {
      await markNotificationRead(id, true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notifications] markRead failed', err);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: false } : n)));
    }
  }, []);

  const markAllRead = useCallback(async () => {
    let snapshot: NotificationItem[] = [];
    setItems((prev) => {
      snapshot = prev;
      return prev.map((n) => (n.read ? n : { ...n, read: true }));
    });
    try {
      await markAllNotificationsRead();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notifications] markAllRead failed', err);
      setItems(snapshot);
    }
  }, []);

  // Initial load.
  useEffect(() => {
    void refetch();
  }, [refetch]);

  // SSE tap — debounced refetch on any event. Notifications are created
  // server-side and the add-in's SSE frame shape for them isn't separable from
  // chat frames, so we treat the stream as a "something happened" signal and
  // source all content from REST.
  const debounceRef = useRef<number | null>(null);
  const onEvent = useCallback(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      void refetch();
    }, REFETCH_DEBOUNCE_MS);
  }, [refetch]);
  useSSE({ onEvent });
  useEffect(
    () => () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    },
    [],
  );

  const value = useMemo<NotificationContextValue>(
    () => ({
      items,
      unreadCount,
      loading,
      hasMore: items.length < totalCount,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      refetch,
      loadMore,
      markRead,
      markAllRead,
    }),
    [
      items,
      unreadCount,
      loading,
      totalCount,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      refetch,
      loadMore,
      markRead,
      markAllRead,
    ],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
